/**
 * Delivery-day presets for the purchase-order review.
 *
 * Restaurant buyers think in days of the week, not in dates: "Wednesday" is
 * the answer, and working out that Wednesday is the 26th is the app's job.
 * So the review offers the next seven days as buttons — the first two named
 * for how near they are, the rest by the standard three-letter weekday
 * abbreviation, which keeps the touch targets wide on a phone.
 *
 * Anything further out is the calendar picker's job; there is deliberately no
 * "next Monday" button, because "Mon" already means the nearest Monday and a
 * second one a week later would be indistinguishable.
 *
 * Dates are LOCAL throughout. `toLocalIsoDate` exists because
 * `Date#toISOString` is UTC — for a user west of Greenwich after 4pm it names
 * tomorrow, which would put "Today" on the wrong day.
 */

const WEEKDAY_ABBR = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const WEEKDAY_FULL = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** `YYYY-MM-DD` in the viewer's own timezone — the format `<input type="date">` speaks. */
export function toLocalIsoDate(date) {
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${month}-${day}`;
}

/** Parse `YYYY-MM-DD` as a LOCAL midnight, not the UTC midnight `new Date(str)` gives. */
export function parseLocalIsoDate(value) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value || '').trim());
  if (!match) return null;
  const d = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * The seven preset buttons, starting today.
 * @param {Date} [today] injectable for tests; defaults to now.
 * @returns {{value:string,label:string,fullLabel:string,weekday:string}[]}
 */
export function buildDeliveryDatePresets(today = new Date()) {
  const base = today instanceof Date ? today : new Date(today);
  const presets = [];
  for (let offset = 0; offset < 7; offset += 1) {
    const d = new Date(base.getFullYear(), base.getMonth(), base.getDate() + offset);
    const weekday = WEEKDAY_ABBR[d.getDay()];
    const label = offset === 0 ? 'Today' : offset === 1 ? 'Tomorrow' : weekday;
    presets.push({
      value: toLocalIsoDate(d),
      label,
      weekday,
      fullLabel: `${WEEKDAY_FULL[d.getDay()]}, ${MONTH_ABBR[d.getMonth()]} ${d.getDate()}`,
    });
  }
  return presets;
}

/**
 * How a chosen delivery date should read once it is set — "Tomorrow ·
 * Tuesday, Aug 25" near-term, plain "Sunday, Sep 6" further out.
 */
export function describeDeliveryDate(value, today = new Date()) {
  const picked = parseLocalIsoDate(value);
  if (!picked) return '';
  const base = today instanceof Date ? today : new Date(today);
  const startOfToday = new Date(base.getFullYear(), base.getMonth(), base.getDate());
  const dayDiff = Math.round((picked.getTime() - startOfToday.getTime()) / 86400000);
  const full = `${WEEKDAY_FULL[picked.getDay()]}, ${MONTH_ABBR[picked.getMonth()]} ${picked.getDate()}`;
  if (dayDiff === 0) return `Today · ${full}`;
  if (dayDiff === 1) return `Tomorrow · ${full}`;
  return full;
}

/** Local wall-clock stamp with a lowercase am/pm — "Aug 23, 2026 · 8:45 p.m." */
export function formatLocalDateTime(value) {
  if (!value) return '';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const date = `${MONTH_ABBR[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
  let hours = d.getHours();
  const meridiem = hours >= 12 ? 'p.m.' : 'a.m.';
  hours = hours % 12 || 12;
  const minutes = String(d.getMinutes()).padStart(2, '0');
  return `${date} · ${hours}:${minutes} ${meridiem}`;
}
